// MapView — mounts the Mapbox GL instance into the shared mapRef (US-875 · AC-1/2/7).
//
// Creates one map centered on APAC (center [100,15], zoom 3, globe projection),
// stores it in MapContext's top-level `mapRef`, and flips `isMapReady` on load so
// LayersContext can apply initial layer visibility. Token + style come from env
// vars (AC-2); David's existing style is the documented demo fallback (replaced by
// the migrated style in EPIC-16 / US-904). Renders an inline error state instead
// of a blank canvas when the token is missing, the style fails (design.md §4 / T7),
// or the browser exposes no WebGL2 (ISSUE-449) — the rest of the app (country
// selection, Analysis panel) keeps working without the map.
import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import useMap from "../../hooks/useMap";
import {
  ANCHOR_CLUSTER_SOURCE,
  ANCHOR_CLUSTER_TIERS,
  ANCHOR_CLUSTER_TYPES,
  CHN_RASTER_TWINS,
  RASTER_SOURCE,
  RASTER_LAYERS,
  REPLACED_VECTOR_LAYERS,
  anchorClusterLayerId,
  rasterPaint,
} from "../../config/layerConfig";

// Every raster layer the map materializes: the toggle-driven gap layers plus
// their China twins (R-11) — same source, same paint contract, same guard.
const RASTER_GAP_ENTRIES = [...RASTER_LAYERS, ...CHN_RASTER_TWINS];

// Demo bridge: David's existing style. EPIC-16 / US-904 swaps this via env.
const DEFAULT_STYLE = "mapbox://styles/mozartatplay/cmnrkgn9o001d01s9hghu7u11";
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAPBOX_STYLE = process.env.REACT_APP_MAPBOX_STYLE || DEFAULT_STYLE;

// Mapbox GL is loaded from its CDN in public/index.html (as window.mapboxgl),
// NOT bundled: CRA/Terser re-minifies Mapbox's already-minified worker blob and
// breaks it in production builds (map renders blank). The CDN copy is untouched.
// Read per mount (not at module load) so its absence is testable.

// Mapbox GL v3 hard-requires WebGL2 and its constructor throws when the browser
// can't create a context — e.g. Chrome with graphics acceleration turned off
// exposes no WebGL at all (ISSUE-449). Probe before constructing so the failure
// becomes an inline message, not an uncaught throw that unmounts the whole app.
// (mapboxgl.supported() was removed in GL JS v2 — a manual probe is the
// documented replacement.)
const isWebglSupported = () => {
  if (!window.WebGL2RenderingContext) return false;
  try {
    return Boolean(document.createElement("canvas").getContext("webgl2"));
  } catch {
    return false;
  }
};

const WEBGL_DISABLED_MESSAGE =
  "Map unavailable — WebGL is disabled in this browser. Enable graphics " +
  "(hardware) acceleration and reload. Country analysis still works without " +
  "the map.";

// US-920 (R-10): the bands available in the TileJSON this client ACTUALLY
// loaded. MRT tiles/TileJSON are HTTP-cached up to 12 h, so right after a
// tileset republish a browser can hold a pre-republish TileJSON that lacks
// the newest bands — a layer bound to a missing band throws MRTError on
// every tile. Returns null while the TileJSON hasn't arrived yet.
const availableRasterBands = (map) => {
  const source = map.getSource && map.getSource(RASTER_SOURCE.id);
  const groups = source && source.rasterLayers;
  if (!Array.isArray(groups)) return null;
  return new Set(groups.flatMap((g) => g?.fields?.bands || []));
};

// US-920 (R-1/R-3/R-10): materialize the continuous raster gap layers from
// config once the style is loaded AND the raster TileJSON delivered its band
// list — one raster layer per entry whose band exists in that list, inserted
// below the first symbol layer so borders/labels render on top. Layers start
// painted off (opacity 0); LayersContext applies the real toggle state on
// markMapReady, which the caller defers until this ran.
const addRasterGapLayers = (map, bands) => {
  const styleLayers = (map.getStyle && map.getStyle()?.layers) || [];
  // A-8 (Lead UX): insert below the style's ROAD layers, not just below the
  // first symbol — the road network drawing on top of the gap surface is
  // what keeps the user oriented once the raster fills the screen. Fallback
  // to the first symbol layer for styles without a road source-layer.
  const firstRoadId = styleLayers.find((l) => l["source-layer"] === "road")?.id;
  const anchorId =
    firstRoadId ?? styleLayers.find((l) => l.type === "symbol")?.id;
  RASTER_GAP_ENTRIES.forEach((entry) => {
    if (!bands.has(entry.band) || map.getLayer(entry.id)) return;
    map.addLayer(
      {
        id: entry.id,
        type: "raster",
        source: RASTER_SOURCE.id,
        "source-layer": RASTER_SOURCE.rasterLayer,
        // Always layout-visible so band chunks load with every tile; painted
        // "off" via raster-opacity 0 until LayersContext applies the state
        // (see RASTER_BASE_OPACITY note in layerConfig).
        layout: { visibility: "visible" },
        paint: { ...rasterPaint(entry), "raster-opacity": 0 },
      },
      anchorId
    );
  });
};

// How long the map may wait for the raster TileJSON before declaring itself
// ready anyway (R-9: a dead raster source must not stall the whole app).
const RASTER_READY_TIMEOUT_MS = 8000;

// US-922 (R-1/R-6): pre-aggregated anchor clusters bridge the anchor_points
// dead zone below z6 — one circle + one label layer per type×tier, generated
// from config, zoom-gated so the real points take over at z6. Layers start
// hidden; the anchor toggles drive them through coupledIds once
// markMapReady applies the state. Guarded and idempotent, independent of the
// raster source.
const addAnchorClusterLayers = (map) => {
  if (!(map.getSource && map.addSource && map.addLayer)) return;
  if (!map.getSource(ANCHOR_CLUSTER_SOURCE.id)) {
    map.addSource(ANCHOR_CLUSTER_SOURCE.id, {
      type: "vector",
      url: ANCHOR_CLUSTER_SOURCE.url,
    });
  }
  const styleLayers = (map.getStyle && map.getStyle()?.layers) || [];
  const firstSymbolId = styleLayers.find((l) => l.type === "symbol")?.id;
  ANCHOR_CLUSTER_TIERS.forEach((tier) => {
    ANCHOR_CLUSTER_TYPES.forEach((type) => {
      const byType = ["==", ["get", "classification"], type.classification];
      const circleId = anchorClusterLayerId(type, tier, "circle");
      if (!map.getLayer(circleId)) {
        map.addLayer(
          {
            id: circleId,
            type: "circle",
            source: ANCHOR_CLUSTER_SOURCE.id,
            "source-layer": tier.sourceLayer,
            minzoom: tier.minzoom,
            maxzoom: tier.maxzoom,
            // R-3: micro-bins below the render threshold never draw — they
            // stay counted in the admin1 bubbles and appear as real points
            // at z6.
            filter: ["all", byType, [">=", ["get", "count"], tier.minRenderCount]],
            layout: { visibility: "none" },
            paint: {
              "circle-color": type.color,
              "circle-opacity": 0.75,
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 1.25,
              // Trefoil offset per type — concurrent types read separately.
              "circle-translate": type.offset,
              // √count sizing; interpolate clamps past the last stop = the
              // cap that absorbs the tail (China's residual bubble).
              "circle-radius": [
                "interpolate",
                ["linear"],
                ["sqrt", ["get", "count"]],
                ...tier.radiusStops.flat(),
              ],
            },
          },
          firstSymbolId
        );
      }
      const labelId = anchorClusterLayerId(type, tier, "label");
      if (!map.getLayer(labelId)) {
        map.addLayer(
          {
            id: labelId,
            type: "symbol",
            source: ANCHOR_CLUSTER_SOURCE.id,
            "source-layer": tier.sourceLayer,
            minzoom: tier.minzoom,
            maxzoom: tier.maxzoom,
            // Labels carry their own (higher) threshold — count rain was the
            // saturation Juan measured with all three types active.
            filter: [
              "all",
              byType,
              [">=", ["get", "count"], Math.max(tier.minRenderCount, tier.minLabelCount)],
            ],
            layout: {
              visibility: "none",
              "text-field": ["get", "label"],
              "text-size": 10,
            },
            paint: {
              "text-color": "#1a2733",
              "text-halo-color": "#ffffff",
              "text-halo-width": 1.2,
              "text-translate": type.offset,
            },
          },
          firstSymbolId
        );
      }
    });
  });
};

// US-920 (R-6): a failed raster tile stays blank and GL never retries it until
// the map moves (spike finding, 2026-07-14). Re-selecting the band re-fetches
// the band chunks (the validated recovery), so on a raster source error we
// bounce each raster layer's band once, with backoff and a bounded budget per
// quiet period ("idle" resets it — a healthy render earns fresh retries).
const RASTER_RETRY_LIMIT = 3;
const createRasterRetrier = (map) => {
  let retries = 0;
  let bounceTimer = null;
  let restoreTimer = null;
  map.on("idle", () => {
    retries = 0;
  });
  return {
    schedule: () => {
      if (retries >= RASTER_RETRY_LIMIT || bounceTimer || restoreTimer) return;
      retries += 1;
      bounceTimer = setTimeout(() => {
        bounceTimer = null;
        RASTER_GAP_ENTRIES.forEach(({ id }) => {
          if (map.getLayer(id)) {
            map.setPaintProperty(id, "raster-array-band", "population");
          }
        });
        restoreTimer = setTimeout(() => {
          restoreTimer = null;
          RASTER_GAP_ENTRIES.forEach(({ id, band }) => {
            if (map.getLayer(id)) {
              map.setPaintProperty(id, "raster-array-band", band);
            }
          });
        }, 60);
      }, 500 * 2 ** retries);
    },
    dispose: () => {
      clearTimeout(bounceTimer);
      clearTimeout(restoreTimer);
    },
  };
};

const MapErrorState = ({ message }) => (
  <Stack
    role="alert"
    alignItems="center"
    justifyContent="center"
    spacing={1}
    sx={{ position: "absolute", inset: 0, p: 3, textAlign: "center" }}
  >
    <MapOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Stack>
);

const MapView = () => {
  const { mapRef, markMapReady, resetMap } = useMap();
  const containerRef = useRef(null);
  const [error, setError] = useState(() => {
    if (!MAPBOX_TOKEN) return "Map unavailable — set REACT_APP_MAPBOX_TOKEN.";
    if (!window.mapboxgl) {
      return "Map library failed to load — check your connection and refresh.";
    }
    if (!isWebglSupported()) return WEBGL_DISABLED_MESSAGE;
    return null;
  });

  useEffect(() => {
    const mapboxgl = window.mapboxgl;
    if (!MAPBOX_TOKEN || !mapboxgl || !containerRef.current) return undefined;
    if (!isWebglSupported()) return undefined;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: MAPBOX_STYLE,
        center: [100, 15],
        zoom: 3,
        projection: "globe",
      });
    } catch {
      // Anything the probe didn't anticipate (constructor throws on any WebGL
      // init failure): degrade to the inline error, never an uncaught throw.
      setError(WEBGL_DISABLED_MESSAGE);
      return undefined;
    }
    mapRef.current = map;

    const rasterRetrier = createRasterRetrier(map);
    let loaded = false;
    let readyTimer = null;
    let ready = false;
    // markMapReady is DEFERRED until the raster layers exist (R-10): if it
    // fired at style load, LayersContext would apply the toggle state before
    // the layers were added and the gaps would stay painted off until the
    // next state change. The timeout keeps a dead raster source from
    // stalling the app (R-9): the map goes ready without raster layers.
    const finishReady = () => {
      if (ready) return;
      ready = true;
      clearTimeout(readyTimer);
      markMapReady();
    };
    const tryMaterializeRaster = () => {
      const bands = availableRasterBands(map);
      if (!bands) return false;
      addRasterGapLayers(map, bands);
      finishReady();
      return true;
    };
    map.on("load", () => {
      loaded = true;
      if (!(map.getSource && map.getSource(RASTER_SOURCE.id))) {
        map.addSource(RASTER_SOURCE.id, {
          type: "raster-array",
          url: RASTER_SOURCE.url,
        });
      }
      // The replaced vector gap layers are forced hidden right away (kept in
      // the style: rollback is a visibility flip) — they must not flash while
      // the raster TileJSON loads.
      REPLACED_VECTOR_LAYERS.forEach((id) => {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, "visibility", "none");
        }
      });
      // Anchor clusters materialize synchronously (their own vector source,
      // no band metadata to wait for) so they exist before markMapReady's
      // initial state sync.
      addAnchorClusterLayers(map);
      if (tryMaterializeRaster()) return;
      const onSourceData = (e) => {
        if (e?.sourceId !== RASTER_SOURCE.id) return;
        if (tryMaterializeRaster() && map.off) map.off("sourcedata", onSourceData);
      };
      map.on("sourcedata", onSourceData);
      readyTimer = setTimeout(() => {
        if (map.off) map.off("sourcedata", onSourceData);
        finishReady();
      }, RASTER_READY_TIMEOUT_MS);
    });
    map.on("error", (e) => {
      // US-920 (R-9): source-scoped errors (a raster tile hiccup, a slow
      // tileset) must never blank a WORKING map — recover instead. Only
      // auth failures or errors before the style ever loaded are fatal.
      if (e?.sourceId === RASTER_SOURCE.id) {
        rasterRetrier.schedule();
        return;
      }
      const status = e?.error?.status;
      if (status === 401 || status === 403 || (!loaded && e?.error?.message)) {
        setError("Map failed to load — check the Mapbox token and style.");
      }
    });

    return () => {
      clearTimeout(readyTimer);
      rasterRetrier.dispose();
      map.remove();
      mapRef.current = null;
      resetMap();
    };
  }, [mapRef, markMapReady, resetMap]);

  return (
    <Box sx={{ position: "absolute", inset: 0 }}>
      <Box ref={containerRef} sx={{ position: "absolute", inset: 0 }} />
      {error && <MapErrorState message={error} />}
    </Box>
  );
};

export default MapView;
